import { useMemo } from "react";
import { CurrencyCode, DEFAULT_CURRENCY } from "./currencies";
import { useAuth } from "./useAuth";

// Base currency is USD
const BASE_CURRENCY: CurrencyCode = "USD";

// Approximate exchange rates relative to USD (1 USD = X Currency)
// As of September 2026 (static values for this implementation)
export const EXCHANGE_RATES: Record<CurrencyCode, number> = {
  USD: 1,
  INR: 95.9375,
  AED: 3.6725,
  EUR: 0.879353,
  GBP: 0.756802,
  SAR: 3.7547,
  QAR: 3.64515,
  KWD: 0.3077,
  BHD: 0.37697,
  OMR: 0.385,
  SGD: 1.27925,
  AUD: 1.42633,
  CAD: 1.4148,
  JPY: 158.385,
  CNY: 6.71318,
};

/**
 * Gets the exchange rate between two currencies.
 * Formula: (1 / Rate_From_USD) * Rate_To_USD
 *
 * Example: Convert EUR to INR
 * 1 USD = 0.879353 EUR  => 1 EUR = 1/0.879353 USD
 * 1 USD = 95.9375 INR
 * 1 EUR = (1/0.879353) * 95.9375 INR ≈ 109.10 INR
 */
export function getExchangeRate(
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number {
  if (fromCurrency === toCurrency) return 1;

  const fromRate = EXCHANGE_RATES[fromCurrency];
  const toRate = EXCHANGE_RATES[toCurrency];

  // Fallback if rate is missing (shouldn't happen with typed keys, but good for safety)
  if (!fromRate || !toRate) {
    console.warn(
      `Missing exchange rate for ${fromCurrency} or ${toCurrency}. Defaulting to 1.`
    );
    return 1;
  }

  return (1 / fromRate) * toRate;
}

/**
 * Converts an amount from one currency to another.
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number {
  if (amount === 0) return 0;
  const rate = getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
}

/**
 * A React hook that automatically converts an amount to the current user's reporting currency.
 * If the user is not authenticated or has no reporting currency set, it defaults to USD.
 *
 * @param amount The amount to convert
 * @param fromCurrency The currency of the input amount
 * @returns An object containing the converted amount, the target currency code, and the exchange rate used.
 */
export function useConvertedAmount(
  amount: number,
  fromCurrency: CurrencyCode
) {
  const { authState } = useAuth();

  const targetCurrency: CurrencyCode = useMemo(() => {
    if (authState.type === "authenticated" && authState.user.reportingCurrency) {
      // We cast here because the DB field is string, but we expect it to be a valid CurrencyCode
      // In a real app, we might want to validate this against the CurrencyCode list
      return authState.user.reportingCurrency as CurrencyCode;
    }
    return DEFAULT_CURRENCY;
  }, [authState]);

  const convertedAmount = useMemo(() => {
    return convertCurrency(amount, fromCurrency, targetCurrency);
  }, [amount, fromCurrency, targetCurrency]);

  const exchangeRate = useMemo(() => {
    return getExchangeRate(fromCurrency, targetCurrency);
  }, [fromCurrency, targetCurrency]);

  return {
    amount: convertedAmount,
    currency: targetCurrency,
    rate: exchangeRate,
    isConverted: fromCurrency !== targetCurrency,
  };
}